import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, useMap, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Trophy, RefreshCw, Map as MapIcon, MapPin, Lightbulb, Route, GitMerge, ListChecks, ArrowDownUp, SignpostBig, ChevronUp, ChevronDown } from 'lucide-react';
import tuvisaLines from '../../utils/tuvisaLines.json';
import tuvisaStops from '../../utils/tuvisaStops.json';
import { getRandomLore } from '../../utils/opeLore';

const RecenterAutomatically = ({ coordinates, isStop }) => {
  const map = useMap();
  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      if (isStop) {
        map.flyTo(coordinates[0], 16, { duration: 1.5 });
      } else {
        map.fitBounds(coordinates, { padding: [50, 50] });
      }
    }
  }, [coordinates, isStop, map]);
  return null;
};

const MapTab = () => {
  const [gameMode, setGameMode] = useState('rutas'); // 'rutas' | 'paradas' | 'nudos' | 'transbordos' | 'orden' | 'sentidos'
  
  const [currentLines, setCurrentLines] = useState([]);
  const [currentStop, setCurrentStop] = useState(null);
  const [directionFilter, setDirectionFilter] = useState('0'); // '0' = Ida, '1' = Vuelta
  
  const [options, setOptions] = useState([]);
  const [correctSequenceIds, setCorrectSequenceIds] = useState([]); // For Orden
  const [selectedSingleOption, setSelectedSingleOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]); // For Nudos
  const [hasChecked, setHasChecked] = useState(false); // For Nudos/Orden confirm
  
  const [score, setScore] = useState(0);
  const [totalAttempted, setTotalAttempted] = useState(0);
  const [lore, setLore] = useState("");

  const generateQuestion = () => {
    setSelectedSingleOption(null);
    setSelectedOptions([]);
    setCorrectSequenceIds([]);
    setHasChecked(false);
    setLore("");

    if (!tuvisaLines || !tuvisaStops) return;

    if (gameMode === 'rutas') {
      const validLines = tuvisaLines.filter(l => 
        (l.coordinates && l.coordinates.length > 0) || 
        (l.dir0 && l.dir0.coordinates && l.dir0.coordinates.length > 0)
      );
      const correctIdx = Math.floor(Math.random() * validLines.length);
      const correct = validLines[correctIdx];
      
      const otherOptions = tuvisaLines.filter(l => l.id !== correct.id);
      const wrong = [...otherOptions].sort(() => 0.5 - Math.random()).slice(0, 3);
      
      setCurrentLines([correct]);
      setCurrentStop(null);
      setDirectionFilter('0');
      setOptions([correct, ...wrong].sort(() => 0.5 - Math.random()));
      
    } else if (gameMode === 'paradas') {
      const stop = tuvisaStops[Math.floor(Math.random() * tuvisaStops.length)];
      const correctRouteData = stop.routes[Math.floor(Math.random() * stop.routes.length)];
      const correctRoute = tuvisaLines.find(r => r.id === correctRouteData.id) || correctRouteData;
      
      const stopRouteIds = new Set(stop.routes.map(r => r.id));
      const invalidRoutes = tuvisaLines.filter(r => !stopRouteIds.has(r.id));
      const wrong = [...invalidRoutes].sort(() => 0.5 - Math.random()).slice(0, 3);
      
      setCurrentLines([correctRoute]);
      setCurrentStop(stop);
      setDirectionFilter('0');
      setOptions([correctRoute, ...wrong].sort(() => 0.5 - Math.random()));
      
    } else if (gameMode === 'nudos') {
      const multiStops = tuvisaStops.filter(s => s.routes.length >= 2);
      const stop = multiStops[Math.floor(Math.random() * multiStops.length)];
      const correctRoutes = stop.routes.map(sr => tuvisaLines.find(r => r.id === sr.id) || sr);
      
      const invalid = tuvisaLines.filter(r => !stop.routes.some(sr => sr.id === r.id));
      const wrongCount = Math.max(8 - correctRoutes.length, 3);
      const wrongRoutes = [...invalid].sort(()=>0.5-Math.random()).slice(0, wrongCount);
      
      setCurrentLines([]);
      setCurrentStop(stop);
      setDirectionFilter('0');
      setOptions([...correctRoutes, ...wrongRoutes].sort(() => 0.5 - Math.random()));
      
    } else if (gameMode === 'transbordos') {
      const multiStops = [...tuvisaStops.filter(s => s.routes.length >= 2)].sort(()=>0.5-Math.random());
      const stop = multiStops[0];
      const r1Data = stop.routes[0];
      const r2Data = stop.routes[1];
      const r1 = tuvisaLines.find(r => r.id === r1Data.id) || r1Data;
      const r2 = tuvisaLines.find(r => r.id === r2Data.id) || r2Data;
      
      const wrongStopsRaw = tuvisaStops.filter(s => 
         !(s.routes.some(r=>r.id===r1.id) && s.routes.some(r=>r.id===r2.id))
      );
      const wrongStops = [...wrongStopsRaw].sort(()=>0.5-Math.random()).slice(0, 3);
      
      setCurrentLines([r1, r2]);
      setCurrentStop(null);
      setDirectionFilter('0');
      setOptions([stop, ...wrongStops].sort(()=>0.5-Math.random()));
      
    } else if (gameMode === 'orden') {
      // Pick route with > 5 stops
      const validLines = tuvisaLines.filter(r => r.dir0?.stops?.length >= 5 || r.dir1?.stops?.length >= 5);
      const route = validLines[Math.floor(Math.random() * validLines.length)];
      const dir = route.dir1?.stops?.length >= 5 && Math.random() > 0.5 ? '1' : '0';
      const stopList = dir === '0' ? route.dir0.stops : route.dir1.stops;
      
      // Get 4 random indices, sorted
      let indices = new Set();
      let panicTimeout = 0;
      while(indices.size < 4 && indices.size < stopList.length && panicTimeout < 100) {
         indices.add(Math.floor(Math.random() * stopList.length));
         panicTimeout++;
      }
      const sortedIndices = Array.from(indices).sort((a,b)=>a-b);
      const selectedStops = sortedIndices.map(idx => stopList[idx]);
      
      setCurrentLines([route]);
      setCurrentStop(null);
      setDirectionFilter(dir);
      setCorrectSequenceIds(selectedStops.map(s => s.id));
      setOptions([...selectedStops].sort(()=>0.5-Math.random())); // shuffle for user
      
    } else if (gameMode === 'sentidos') {
      // Pick a route
      const validLines = tuvisaLines.filter(r => r.dir0?.stops?.length > 0 && r.dir1?.stops?.length > 0);
      const r = validLines[Math.floor(Math.random() * validLines.length)] || tuvisaLines[0];
      const stops0 = r.dir0?.stops || [];
      const stops1 = r.dir1?.stops || [];
      const s0Ids = new Set(stops0.map(s=>s.id));
      const s1Ids = new Set(stops1.map(s=>s.id));
      
      // Prefer exclusive stops
      let chosenStop = null;
      let rand = Math.random();
      
      const onlyIda = stops0.filter(s => !s1Ids.has(s.id));
      const onlyVuelta = stops1.filter(s => !s0Ids.has(s.id));
      const both = stops0.filter(s => s1Ids.has(s.id));
      
      if (rand < 0.33 && onlyIda.length > 0) chosenStop = onlyIda[Math.floor(Math.random()*onlyIda.length)];
      else if (rand < 0.66 && onlyVuelta.length > 0) chosenStop = onlyVuelta[Math.floor(Math.random()*onlyVuelta.length)];
      else chosenStop = both[Math.floor(Math.random()*both.length)];
      
      if (!chosenStop && stops0.length) chosenStop = stops0[0];

      setCurrentLines([r]);
      setCurrentStop(chosenStop);
      setDirectionFilter('0');
      setOptions([
        { id: 'ida', name: 'Sólo a la Ida' },
        { id: 'vuelta', name: 'Sólo a la Vuelta' },
        { id: 'ambos', name: 'En ambos sentidos' }
      ]);
    }
  };

  useEffect(() => {
    generateQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode]);

  // Handle single clicks for Rutas, Paradas, Transbordos, Sentidos
  const handleSingleSelect = (opt) => {
    if (selectedSingleOption || hasChecked) return; 
    setSelectedSingleOption(opt);
    setHasChecked(true); // Treat single select as checked for uniform flow
    setTotalAttempted(prev => prev + 1);
    
    if (gameMode === 'transbordos') {
      const correctStop = options.find(o => o.routes && currentLines.every(cl => o.routes.some(r=>r.id === cl.id)));
      if (opt.id === correctStop?.id) setScore(prev => prev + 1);
    } else if (gameMode === 'sentidos') {
      const stops0 = currentLines[0]?.dir0?.stops || [];
      const stops1 = currentLines[0]?.dir1?.stops || [];
      const inIda = stops0.some(s=>s.id === currentStop.id);
      const inVuelta = stops1.some(s=>s.id === currentStop.id);
      let isCorrect = false;
      if (inIda && !inVuelta && opt.id === 'ida') isCorrect = true;
      if (!inIda && inVuelta && opt.id === 'vuelta') isCorrect = true;
      if (inIda && inVuelta && opt.id === 'ambos') isCorrect = true;
      if (isCorrect) setScore(prev => prev + 1);
    } else {
      if (opt.id === currentLines[0]?.id) {
        setScore(prev => prev + 1);
        setLore(getRandomLore(currentLines[0].id));
      }
    }
  };

  // Nudos
  const toggleMultiSelect = (opt) => {
    if (hasChecked) return;
    if (selectedOptions.includes(opt.id)) setSelectedOptions(selectedOptions.filter(id => id !== opt.id));
    else setSelectedOptions([...selectedOptions, opt.id]);
  };
  const checkNudos = () => {
    setHasChecked(true);
    setTotalAttempted(prev => prev + 1);
    const correctIds = currentStop.routes.map(r => r.id);
    const isCorrect = selectedOptions.length === correctIds.length && selectedOptions.every(id => correctIds.includes(id));
    if (isCorrect) setScore(prev => prev + 1);
  };

  // Orden
  const moveOption = (index, shift) => {
    if (hasChecked) return;
    const newOptions = [...options];
    const targetIdx = index + shift;
    if (targetIdx < 0 || targetIdx >= newOptions.length) return;
    [newOptions[index], newOptions[targetIdx]] = [newOptions[targetIdx], newOptions[index]];
    setOptions(newOptions);
  };
  const checkOrden = () => {
    setHasChecked(true);
    setTotalAttempted(prev => prev + 1);
    const isCorrect = options.every((opt, i) => opt.id === correctSequenceIds[i]);
    if (isCorrect) {
      setScore(prev => prev + 1);
      setLore(getRandomLore(currentLines[0]?.id));
    }
  };


  // Loading Safeties
  if (gameMode === 'rutas' && currentLines.length === 0) return <div className="p-4 text-center">Cargando rutas...</div>;
  if ((gameMode === 'paradas' || gameMode === 'nudos') && !currentStop) return <div className="p-4 text-center">Cargando paradas...</div>;
  if (gameMode === 'transbordos' && currentLines.length < 2) return <div className="p-4 text-center">Calculando transbordos...</div>;

  // Title Logic
  let questionText = "";
  if (gameMode === 'rutas') questionText = "¿Qué línea es esta?";
  if (gameMode === 'paradas') questionText = `¿Qué línea pasa por la parada "${currentStop?.name}"?`;
  if (gameMode === 'nudos') questionText = `Selecciona TODAS las líneas que paran en "${currentStop?.name}"`;
  if (gameMode === 'transbordos') questionText = `¿En qué parada puedes hacer transbordo entre la L${currentLines[0]?.id} y la L${currentLines[1]?.id}?`;
  if (gameMode === 'orden') questionText = `Ordena cronológicamente estas paradas de la L${currentLines[0]?.id} (Dirección ${directionFilter === '0' ? 'Ida' : 'Vuelta'})`;
  if (gameMode === 'sentidos') questionText = `Si haces la L${currentLines[0]?.id}, ¿cuándo pasas por "${currentStop?.name}"?`;

  let centerCoords = [];
  if (gameMode === 'rutas' || gameMode === 'transbordos' || gameMode === 'orden' || gameMode === 'sentidos') {
    const dirKey = directionFilter === '0' ? 'dir0' : 'dir1';
    centerCoords = currentLines.flatMap(l => (l[dirKey]?.coordinates?.length ? l[dirKey].coordinates : l.coordinates) || []);
  } else {
    centerCoords = [[currentStop?.lat, currentStop?.lng]];
  }

  // Answer checking logic for results popup
  let userWon = false;
  if (gameMode === 'transbordos') {
      const correctStopForTransbordos = options.find(o => o.routes && currentLines.every(cl => o.routes.some(r=>r.id === cl.id)));
      userWon = selectedSingleOption?.id === correctStopForTransbordos?.id;
  } else if (gameMode === 'sentidos') {
      const stops0 = currentLines[0]?.dir0?.stops || [];
      const stops1 = currentLines[0]?.dir1?.stops || [];
      const inIda = stops0.some(s=>s.id === currentStop?.id);
      const inVuelta = stops1.some(s=>s.id === currentStop?.id);
      if (inIda && !inVuelta && selectedSingleOption?.id === 'ida') userWon = true;
      if (!inIda && inVuelta && selectedSingleOption?.id === 'vuelta') userWon = true;
      if (inIda && inVuelta && selectedSingleOption?.id === 'ambos') userWon = true;
  } else if (gameMode === 'nudos') {
      const correctNudoIds = currentStop?.routes.map(r => r.id) || [];
      userWon = hasChecked && selectedOptions.length === correctNudoIds.length && selectedOptions.every(id => correctNudoIds.includes(id));
  } else if (gameMode === 'orden') {
      userWon = hasChecked && options.every((opt, i) => opt.id === correctSequenceIds[i]);
  } else {
      userWon = selectedSingleOption?.id === currentLines[0]?.id;
  }
  
  const isGameOver = hasChecked;

  return (
    <div className="space-y-4">
      {/* Top Bar for Score & Mode */}
      <div className="flex flex-col xl:flex-row justify-between items-center bg-card p-4 rounded-xl border shadow-sm gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <MapIcon className="text-primary w-5 h-5 hidden xl:block" />
          <h2 className="font-semibold text-lg hidden xl:block">OPE Tuvisa</h2>
        </div>
        
        <div className="flex flex-wrap p-1 bg-muted rounded-lg w-full xl:w-auto gap-1">
          {['rutas', 'paradas', 'nudos', 'transbordos', 'orden', 'sentidos'].map(mode => {
            const icons = { 'rutas': Route, 'paradas': MapPin, 'nudos': ListChecks, 'transbordos': GitMerge, 'orden': ArrowDownUp, 'sentidos': SignpostBig };
            const labels = { 'rutas': 'Trazados', 'paradas': 'Paradas', 'nudos': 'Nudos', 'transbordos': 'Transbordos', 'orden': 'Orden', 'sentidos': 'Sentidos' };
            const Icon = icons[mode];
            return (
              <button key={mode} onClick={() => setGameMode(mode)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all min-w-[100px] ${gameMode === mode ? 'bg-background shadow-sm text-foreground ring-1 ring-border/50' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
              >
                <Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{labels[mode]}</span>
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full shrink-0">
          <Trophy className="text-primary w-4 h-4" />
          <span className="font-bold text-primary">{score} / {totalAttempted}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Map View */}
        <div className="bg-card text-card-foreground rounded-xl border shadow-sm overflow-hidden h-[450px] relative z-0">
          <MapContainer center={[42.8465, -2.6716]} zoom={13} scrollWheelZoom={true} className="w-full h-full">
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            
            {/* Draw current lines */}
            {currentLines.map(line => {
              const dirKey = directionFilter === '0' ? 'dir0' : 'dir1';
              const coords = (line[dirKey]?.coordinates?.length ? line[dirKey].coordinates : line.coordinates) || [];
              return coords.length > 0 && <Polyline key={line.id} positions={coords} pathOptions={{ color: line.color || '#3388ff', weight: 6, opacity: 0.8 }} />;
            })}

            {/* Draw current stop if mode requires it */}
            {(gameMode === 'paradas' || gameMode === 'nudos' || gameMode === 'sentidos') && currentStop && (
               <CircleMarker center={[currentStop.lat, currentStop.lng]} pathOptions={{ color: 'red', fillColor: '#f03', fillOpacity: 0.5 }} radius={12}>
                  <Tooltip permanent direction="top" opacity={0.9} className="font-bold text-sm">
                    {currentStop.name}
                  </Tooltip>
               </CircleMarker>
            )}

            <RecenterAutomatically coordinates={centerCoords} isStop={gameMode === 'paradas' || gameMode === 'nudos' || gameMode === 'sentidos'} />
          </MapContainer>
        </div>

        {/* Game Controls */}
        <div className="bg-card text-card-foreground rounded-xl border shadow-sm flex flex-col h-[450px] overflow-y-auto">
          <div className="flex flex-col space-y-1.5 p-6 pb-2 shrink-0">
            <h3 className="font-bold leading-tight tracking-tight text-lg">{questionText}</h3>
          </div>
          <div className="p-6 pt-2 flex-1 flex flex-col gap-2 relative">
            
            <div className={`grid gap-2 ${gameMode === 'nudos' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {options.map((opt, index) => {
                let btnClass = "py-4 text-sm sm:text-base justify-start px-4 outline-none ";
                
                // ORDEN
                if (gameMode === 'orden') {
                  const isCorrectPos = hasChecked && correctSequenceIds[index] === opt.id;
                  const isWrongPos = hasChecked && correctSequenceIds[index] !== opt.id;
                  if (isCorrectPos) btnClass += " bg-green-500 text-white border-green-600";
                  else if (isWrongPos) btnClass += " bg-red-500 text-white border-red-600";
                  else btnClass += " border-input bg-background";
                  
                  return (
                    <div key={opt.id} className={`flex items-center rounded-md font-medium transition-colors border shadow-sm overflow-hidden ${btnClass}`}>
                      <div className="flex flex-col border-r bg-black/5 dark:bg-white/5">
                        <button onClick={()=>moveOption(index, -1)} disabled={index===0 || hasChecked} className="p-1.5 hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-20"><ChevronUp className="w-5 h-5"/></button>
                        <button onClick={()=>moveOption(index, 1)} disabled={index===options.length-1 || hasChecked} className="p-1.5 hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-20 border-t"><ChevronDown className="w-5 h-5"/></button>
                      </div>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center font-bold ml-3 mr-3 shrink-0 text-xs bg-primary text-primary-foreground border-primary/20">{index + 1}</span>
                      <span className="truncate pr-3">{opt.name}</span>
                      {hasChecked && isCorrectPos && <span className="ml-auto pr-3 font-bold">✓</span>}
                      {hasChecked && isWrongPos && <span className="ml-auto pr-3 font-bold opacity-75 text-xs">Era Nº {correctSequenceIds.indexOf(opt.id)+1}</span>}
                    </div>
                  );
                }

                // NUDOS
                if (gameMode === 'nudos') {
                  const isSelected = selectedOptions.includes(opt.id);
                  const correctNudoIds = currentStop?.routes.map(r => r.id) || [];
                  const isActuallyCorrect = correctNudoIds.includes(opt.id);
                  
                  if (!hasChecked) btnClass += isSelected ? " ring-2 ring-primary bg-primary/10 border-primary" : " border-input bg-background hover:bg-accent";
                  else {
                    if (isActuallyCorrect && isSelected) btnClass += " bg-green-500 text-white border-green-600";
                    else if (isActuallyCorrect && !isSelected) btnClass += " ring-2 ring-green-500 bg-green-100 border-green-500 text-green-700 opacity-80";
                    else if (!isActuallyCorrect && isSelected) btnClass += " bg-red-500 text-white border-red-600";
                    else btnClass += " opacity-40 border-input bg-background";
                  }
                  
                  return (
                    <button key={opt.id} className={`inline-flex items-center rounded-md font-medium transition-colors border shadow-sm disabled:pointer-events-none disabled:opacity-50 ${btnClass}`} onClick={() => toggleMultiSelect(opt)} disabled={hasChecked}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center font-bold mr-2 shrink-0 text-xs" style={{ backgroundColor: opt.color, color: 'white' }}>{opt.id}</span>
                      <span className="truncate">{opt.name}</span>
                    </button>
                  );
                } 

                // SINGLE SELECT (Rutas, Paradas, Transbordos, Sentidos)
                const isSelectedOptionThis = selectedSingleOption?.id === opt.id;
                let isRightTarget = false;
                if (gameMode === 'transbordos') {
                  const correctStopForTransbordos = options.find(o => o.routes && currentLines.every(cl => o.routes.some(r=>r.id === cl.id)));
                  isRightTarget = correctStopForTransbordos?.id === opt.id;
                } else if (gameMode === 'sentidos') {
                  const inIda = currentLines[0]?.dir0?.stops?.some(s=>s.id === currentStop?.id);
                  const inVuelta = currentLines[0]?.dir1?.stops?.some(s=>s.id === currentStop?.id);
                  if (inIda && !inVuelta && opt.id === 'ida') isRightTarget = true;
                  if (!inIda && inVuelta && opt.id === 'vuelta') isRightTarget = true;
                  if (inIda && inVuelta && opt.id === 'ambos') isRightTarget = true;
                } else {
                  isRightTarget = currentLines[0]?.id === opt.id;
                }

                if (hasChecked) {
                  if (isRightTarget) btnClass += " bg-green-500 text-white border-green-600";
                  else if (isSelectedOptionThis) btnClass += " bg-red-500 text-white border-red-600";
                  else btnClass += " opacity-60 border-input bg-background";
                } else {
                  btnClass += " hover:bg-accent hover:text-accent-foreground border-input bg-background";
                }

                return (
                  <button key={opt.id} className={`inline-flex items-center rounded-md font-medium transition-colors border shadow-sm disabled:pointer-events-none disabled:opacity-50 ${btnClass}`} onClick={() => handleSingleSelect(opt)} disabled={hasChecked}>
                    {opt.color ? (
                      <span className="w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 shrink-0" style={{ backgroundColor: opt.color, color: 'white' }}>{opt.id}</span>
                    ) : (
                      <MapPin className="w-5 h-5 mr-3 shrink-0 text-muted-foreground" />
                    )}
                    <span className="line-clamp-2 text-left leading-tight">{opt.name}</span>
                  </button>
                );
              })}
            </div>

            {gameMode === 'nudos' && !hasChecked && (
              <button onClick={checkNudos} disabled={selectedOptions.length === 0} className="mt-4 w-full bg-primary text-primary-foreground h-10 rounded-md font-bold disabled:opacity-50">
                Comprobar {selectedOptions.length} seleccionadas
              </button>
            )}

            {gameMode === 'orden' && !hasChecked && (
              <button onClick={checkOrden} className="mt-4 w-full bg-primary text-primary-foreground h-10 rounded-md font-bold transition-all hover:bg-primary/90">
                Comprobar Orden
              </button>
            )}

            {isGameOver && (
              <div className="mt-auto pt-4 flex flex-col items-center gap-3 animate-in fade-in zoom-in duration-300">
                <div className={`text-lg font-bold ${userWon ? 'text-green-600' : 'text-red-500'}`}>
                  {userWon ? '¡Correcto! 🎉' : 'Incorrecto o incompleto.'}
                </div>
                
                {userWon && lore && (
                  <div className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 p-3 rounded-lg flex gap-3 items-start border border-amber-200 dark:border-amber-800 w-full mb-2">
                    <Lightbulb className="w-5 h-5 shrink-0 mt-0.5" />
                    <p className="text-sm font-medium leading-tight">{lore}</p>
                  </div>
                )}

                <button onClick={generateQuestion} className="inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-8 w-full gap-2 text-base shadow-sm">
                  <RefreshCw className="w-4 h-4" /> Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapTab;
